var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { PdfFontStyle, PdfTrueTypeFont } from '@syncfusion/ej2-pdf';
/**
 *
 * @hidden
 */
export class PdfViewerUtils {
    /**
     * It returns a boolean value.
     *
     * @param {string} text - It get the fontcollection.
     * @param {PdfTrueTypeFont} font - Get the font.
     * @private
     * @returns {boolean} - Return the boolean.
     */
    static isSupportedFont(text, font) {
        try {
            if (!isNullOrUndefined(text) && !isNullOrUndefined(font)) {
                for (let i = 0; i < text.length; i++) {
                    const ch = text[parseInt(i.toString(), 10)];
                    font._fontInternal._ttfReader._getGlyph(ch);
                    if (!font._fontInternal._ttfReader._isFontPresent && (ch !== ' ') && !this.hasEscapeSequences(ch)) {
                        return font._fontInternal._ttfReader._isFontPresent;
                    }
                }
                return true;
            }
        }
        catch (e) {
            return false;
        }
        return false;
    }
    /**
     * Checks if the given character is an escape sequence.
     *
     * @param {string} char - The character to be checked.
     * @private
     * @returns {boolean} - Returns true if the character is an escape sequence, otherwise false.
     */
    static hasEscapeSequences(char) {
        const escapeRegex = /[\0\b\t\n\v\f\r'"\\]/;
        return escapeRegex.test(char);
    }
    /**
     * It convert byte array to base64 string.
     *
     * @param {any} fontStream - It get the byte array.
     * @private
     * @returns {any} - Return the base64 string.
     */
    static processFontStream(fontStream) {
        return fontStream instanceof Uint8Array ? this.convertByteArrayToBase64(fontStream) : fontStream;
    }
    /**
     * @param {string} customFonts - Get the custom fonts.
     * @param {string} url - Get the url.
     * @returns {any} - It's return fontCollection
     * @private
     */
    static fetchCustomFonts(customFonts, url) {
        const fontCollection = {};
        const fontPromises = customFonts.map((font) => {
            let fontPath;
            if (font.startsWith('http://') || font.startsWith('https://')) {
                fontPath = font;
            }
            else {
                fontPath = `${url}/${font}`;
            }
            const parts = fontPath.split('/');
            const fileName = parts.pop() || '';
            if (Object.keys(fontCollection).indexOf(fileName) === -1) {
                return this.fetchData(fontPath).then((fontData) => __awaiter(this, void 0, void 0, function* () {
                    if (fontData) {
                        fontCollection[parts.indexOf('fallbackfonts') !== -1 ?
                            'fallbackfonts_' + fileName.toLowerCase() : fileName.toLowerCase()] = fontData;
                    }
                    else {
                        const fallbackFontPath = `${url}/fallbackfonts/${font}`;
                        return this.fetchData(fallbackFontPath).then((fallbackData) => {
                            if (fallbackData) {
                                fontCollection['fallbackfonts_' + fileName.toLowerCase()] = fallbackData;
                            }
                        });
                    }
                }));
            }
            return null;
        });
        return Promise.all(fontPromises).then(() => fontCollection);
    }
    /**
     * @param {any} fontCollection - Get the custom fonts collection.
     * @param {string} text - Get the font family.
     * @param {number} fontSize - Get the font size.
     * @param {PdfFontStyle} fontStyle - Get the font style.
     * @returns {any} - It's return fontCollection
     * @private
     */
    static tryGetFontFromKeys(fontCollection, text, fontSize, fontStyle) {
        const keys = Object.keys(fontCollection);
        for (const key of keys) {
            let fontStream = this.processFontStream(fontCollection[`${key}`]);
            let font = new PdfTrueTypeFont(fontStream, this.convertPixelToPoint(fontSize), fontStyle ? fontStyle : PdfFontStyle.regular);
            const glyphPresent = this.isSupportedFont(text, font);
            if (glyphPresent) {
                return font;
            }
            font = null;
            fontStream = null;
        }
        return null;
    }
    /**
     * @param {any} fallbackFontCollection - Get the custom fonts collection.
     * @param {string} fontFamily - Get the font family.
     * @returns {any} - It's return fontCollection
     * @private
     */
    static getFontKey(fallbackFontCollection, fontFamily) {
        if (fallbackFontCollection[`${fontFamily}`] || fallbackFontCollection[fontFamily + '.ttf']) {
            return fallbackFontCollection[`${fontFamily}`] ? fontFamily : fontFamily + '.ttf';
        }
        const font = fontFamily.endsWith('.ttf') ? fontFamily : fontFamily + '.ttf';
        for (const key in fallbackFontCollection) {
            if (key.toLowerCase().endsWith(font.toLowerCase())) {
                return key;
            }
        }
        return undefined;
    }
    /**
     * @param {string} color - Gets the color in hex RGBA pattern.
     * @returns {boolean} - It's return boolean
     * @private
     */
    static isHexRGBAAndTransparent(color) {
        // Check if the string matches the #RRGGBBAA pattern
        const hexRGBARegex = /^#([A-Fa-f0-9]{8})$/;
        if (!hexRGBARegex.test(color)) {
            return false; // Not a valid hex RGBA
        }
        // Extract the alpha value (last two characters)
        const alphaHex = color.slice(-2); // Last two characters
        const alphaDecimal = parseInt(alphaHex, 16); // Convert to decimal
        // Check if alpha is zero (transparent)
        return alphaDecimal === 0;
    }
    /**
     * @param {string} color - Gets the background color with transparency.
     * @returns {string} - It's return background color with transparency.
     * @private
     */
    static setTransparencyToHex(color) {
        if (color.includes('#')) {
            if (color.length > 8 && color !== '#00000000') {
                color = color.slice(0, -2) + '60';
            }
            else {
                color += '60';
            }
        }
        return color;
    }
    /**
     * @param {string} color - Gets the background color without transparency.
     * @returns {string} - It's return background color without transparency.
     * @private
     */
    static removeAlphaValueFromHex(color) {
        if (color.includes('#')) {
            if (color.length > 8) {
                color = color.slice(0, -2);
            }
        }
        return color;
    }
    static fetchData(filePath) {
        return fetch(filePath)
            .then((response) => {
            if (!response.ok) {
                return null;
            }
            return response.arrayBuffer().then((buffer) => new Uint8Array(buffer));
        })
            .catch(() => null);
    }
    static convertByteArrayToBase64(byteArray) {
        let binaryString = '';
        const byteArrayLength = byteArray.byteLength;
        for (let i = 0; i < byteArrayLength; i++) {
            binaryString += String.fromCharCode(byteArray[parseInt(i.toString(), 10)]);
        }
        return btoa(binaryString);
    }
    /**
     * @private
     * @param {number} value - It describes about the value
     * @returns {number} - number
     */
    static convertPixelToPoint(value) {
        return (value * 72 / 96);
    }
    /**
     * Method to deep-shallow copy an object, only if it is a Proxy
     *
     * @private
     * @param {any} obj - Get the data of the next queued task.
     * @returns {any} - The copied object if it was a Proxy; otherwise, returns the original object.
     */
    static cloneProxy(obj) {
        if (this.isProxy(obj)) {
            const copy = Object.assign({}, obj);
            for (const key in copy) {
                // eslint-disable-next-line security/detect-object-injection
                if (this.isProxy(copy[key])) {
                    // eslint-disable-next-line security/detect-object-injection
                    copy[key] = this.cloneProxy(copy[key]); // Recursively process each property
                }
            }
            return copy;
        }
        return obj;
    }
    /**
     * Method to check if a value is a plain object (Proxy detection)
     *
     * @private
     * @param {any} value - Get the data of the next queued task.
     * @returns {boolean} - Returns true if the value is a Proxy; otherwise, false.
     */
    static isProxy(value) {
        return Object.prototype.toString.call(value) === '[object Object]';
    }
    /**
     * @private
     * @returns {string} - string
     */
    static createGUID() {
        return this.getRandomNumber();
    }
    static getRandomNumber() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const random = Math.random() * 16 | 0;
            const v = c === 'x' ? random : (random & 0x3 | 0x8);
            return random.toString(16);
        });
    }
}
/**
 *
 * @hidden
 */
export class PdfViewerSessionStorage {
    // Constructor
    constructor(enableLocalStorage) {
        // Fields
        this.localStorage = {};
        /**
         * @private
         */
        this.enableLocalStorage = true;
        /**
         * @private
         */
        this.documentId = '';
        // eslint-disable-next-line camelcase
        this.enableLocalStorage = enableLocalStorage;
    }
    /**
     * Method to set an item in either session or local storage
     *
     * @param {string} key - Get the key.
     * @param {string} value - Get the value.
     * @private
     * @returns {void}
     */
    setItem(key, value) {
        if (this.enableLocalStorage) {
            this.localStorage[`${key}`] = value;
        }
        else {
            window.sessionStorage.setItem(key, value);
        }
    }
    /**
     * Method to get an item from either session or local storage
     *
     * @param {string} key - Get the key.
     * @private
     * @returns {any} - It's return the value.
     */
    getItem(key) {
        if (this.enableLocalStorage) {
            return (key in this.localStorage) ? this.localStorage[`${key}`] : null;
        }
        else {
            return window.sessionStorage.getItem(key);
        }
    }
    /**
     * Method to remove an item from either session or local storage
     *
     * @param {string} key - Get the key.
     * @private
     * @returns {void}
     */
    removeItem(key) {
        if (this.enableLocalStorage) {
            delete this.localStorage[`${key}`];
        }
        else {
            window.sessionStorage.removeItem(key);
        }
    }
    /**
     * Method to return the length of the storage
     *
     * @private
     * @returns {number} - return the length of the session.
     */
    getSessionLength() {
        if (this.enableLocalStorage) {
            return Object.keys(this.localStorage).length;
        }
        else {
            return window.sessionStorage.length;
        }
    }
    /**
     * Method to return the key at the specified index
     *
     * @param {number} index - Get the index.
     * @private
     * @returns {any} - It's return the key value.
     */
    getKey(index) {
        if (this.enableLocalStorage) {
            const keys = Object.keys(this.localStorage);
            return keys[parseInt(index.toString(), 10)] || null;
        }
        else {
            return window.sessionStorage.key(index);
        }
    }
    /**
     * Method to return the window session storage size.
     *
     * @private
     * @returns {any} - It's return the key value.
     */
    getWindowSessionStorageSize() {
        return Math.round(JSON.stringify(window.sessionStorage).length / 1024);
    }
    /**
     * Method to move all items from sessionStorage to localStorage
     *
     * @param {boolean} enableLocalStorage - Get the enableLocalStorage value.
     * @private
     * @returns {void}
     */
    migrateToLocalStorage(enableLocalStorage) {
        // eslint-disable-next-line camelcase
        this.enableLocalStorage = enableLocalStorage;
        if (this.enableLocalStorage) {
            // Move all session storage items to local storage
            let removingItems = this.getRemovingItems();
            for (let i = 0; i < removingItems.length; i++) {
                const key = removingItems[parseInt(i.toString(), 10)];
                if (key) {
                    const value = window.sessionStorage.getItem(key);
                    if (!isNullOrUndefined(value) && this.documentId !== '' && key.indexOf(this.documentId) !== -1) {
                        this.localStorage[`${key}`] = value;
                        window.sessionStorage.removeItem(key);
                    }
                }
            }
            removingItems = null;
        }
    }
    /**
     * Method to clear all viewer items from sessionStorage and localStorage.
     *
     * @private
     * @returns {void}
     */
    clear() {
        if (this.enableLocalStorage) {
            this.localStorage = {};
        }
        else {
            let removingItems = this.getRemovingItems();
            for (let i = 0; i < removingItems.length; i++) {
                const key = removingItems[parseInt(i.toString(), 10)];
                if (key.indexOf('Sync_PdfViewer_') !== -1) {
                    window.sessionStorage.removeItem(key);
                }
            }
            removingItems = null;
        }
    }
    getRemovingItems() {
        const keysToProcess = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
            const key = window.sessionStorage.key(i);
            if (key) {
                keysToProcess.push(key);
            }
        }
        return keysToProcess;
    }
}
/**
 *
 * @hidden
 */
export var TaskPriorityLevel;
(function (TaskPriorityLevel) {
    TaskPriorityLevel[TaskPriorityLevel["High"] = 1] = "High";
    TaskPriorityLevel[TaskPriorityLevel["Medium"] = 2] = "Medium";
    TaskPriorityLevel[TaskPriorityLevel["Low"] = 3] = "Low";
})(TaskPriorityLevel || (TaskPriorityLevel = {}));
/**
 *
 * @hidden
 */
export class PdfiumTaskScheduler {
    // Constructor
    constructor(workerScript, pdfViewer) {
        this.taskQueue = [];
        this.isProcessing = false;
        this.functionManager = {};
        this.worker = new Worker(workerScript);
        this.taskQueue = [];
        this.isProcessing = false;
        this.pdfViewer = pdfViewer;
        this.functionManager = {};
    }
    /**
     * Method to add the given task into request for the worker
     *
     * @param {any} taskData - Get the task data.
     * @param {TaskPriorityLevel} priority - Get the priority level for the task.
     * @private
     * @returns {void}
     */
    addTask(taskData, priority) {
        if (taskData.message === 'unloadFPDF' || taskData.skipOnReload === true) {
            this.taskQueue = [];
            this.functionManager = {};
        }
        this.taskQueue.push({ taskData, priority });
        this.taskQueue.sort((a, b) => a.priority - b.priority); // Sort by priority
        this.processQueue(); // Start processing if idle
    }
    /**
     * Method to remove previously created requests when image size for page organizer is changed
     *
     * @private
     * @param {number} currentImageSize the current image size of page organizer
     * @returns {void}
     */
    removePreviewImageTasks(currentImageSize) {
        /* eslint-disable security/detect-object-injection */
        for (let index = 0; index < this.taskQueue.length;) {
            if (this.taskQueue[index].taskData.imageSize !== currentImageSize) {
                this.taskQueue.splice(index, 1);
            }
            else {
                index++;
            }
        }
        /* eslint-enable security/detect-object-injection */
    }
    /**
     * Method to request posted for the queue task
     *
     * @returns {void}
     */
    processQueue() {
        if (this.isProcessing || this.taskQueue.length === 0) {
            return;
        }
        const nextTask = this.taskQueue.shift();
        this.isProcessing = true;
        const isVue3 = this.pdfViewer.isVue3 || (this.pdfViewer.parent && this.pdfViewer.parent.isVue3);
        const taskData = isVue3 ? PdfViewerUtils.cloneProxy(nextTask.taskData) : nextTask.taskData;
        this.worker.postMessage(taskData);
    }
    /**
     * Method to call on message for the worker
     *
     * @param {string} key - Get the key value for the method function.
     * @param {any} method - Get the method for the onmessage.
     * @private
     * @returns {void}
     */
    onMessage(key, method) {
        if (!Object.prototype.hasOwnProperty.call(this.functionManager, key)) {
            this.functionManager[`${key}`] = method;
        }
        this.worker.onmessage = (event) => {
            if (event.data.message !== '') {
                for (const handlerKey in this.functionManager) {
                    if (Object.prototype.hasOwnProperty.call(this.functionManager, handlerKey) &&
                        handlerKey.includes(event.data.message)) {
                        this.functionManager[`${handlerKey}`](event);
                        break;
                    }
                }
            }
            this.isProcessing = false;
            this.processQueue();
        };
    }
    /**
     * Method to terminate the worker
     *
     * @private
     * @returns {void}
     */
    terminate() {
        this.worker.terminate();
        this.functionManager = {};
    }
}
